import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const ShopContext = createContext(null);

const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function ShopProvider({ children }) {
  const [cart, setCart] = useState(() => read("cg_cart", []));
  const [wishlist, setWishlist] = useState(() => read("cg_wishlist", []));
  const [recentlyViewed, setRecentlyViewed] = useState(() => read("cg_recently_viewed", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => localStorage.setItem("cg_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("cg_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem("cg_recently_viewed", JSON.stringify(recentlyViewed)), [recentlyViewed]);

  // track product page views (most recent first, no duplicates, capped at 12)
  const trackView = (product) => {
    if (!product?.id) return;
    setRecentlyViewed((prev) => {
      const snap = {
        id: product.id, slug: product.slug, name: product.name, price: product.price,
        price_prefix: product.price_prefix, image: product.image, metal: product.metal,
        product_type: product.product_type, stock_status: product.stock_status, featured: product.featured,
      };
      return [snap, ...prev.filter((p) => p.id !== product.id)].slice(0, 12);
    });
  };

  // merge local + server wishlist both ways when signed in (also callable after login)
  const syncAccountWishlist = async () => {
    if (!localStorage.getItem("cg_customer_token")) return;
    try {
      const { api } = await import("../lib/api");
      const local = read("cg_wishlist", []);
      const { data } = await api.get("/account/wishlist");
      const mergedIds = [...new Set([...local.map((i) => i.id), ...data.product_ids])];
      await api.put("/account/wishlist", { product_ids: mergedIds });
      const { data: full } = await api.get("/account/wishlist");
      setWishlist(full.items.map((p) => ({ id: p.id, slug: p.slug, name: p.name })));
    } catch {}
  };

  useEffect(() => {
    syncAccountWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncWishlist = (list) => {
    if (!localStorage.getItem("cg_customer_token")) return;
    import("../lib/api").then(({ api }) =>
      api.put("/account/wishlist", { product_ids: list.map((i) => i.id) }).catch(() => {})
    );
  };

  const addToCart = (product, size = null, qty = 1, personalisation = "", extra = {}) => {
    setCart((prev) => {
      const key = `${product.id}:${size || ""}:${extra.metal || ""}:${extra.chain || ""}:${extra.width || ""}:${personalisation || ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      const price =
        product.product_type === "voucher" && size && /^£(\d+)$/.test(size)
          ? parseFloat(size.slice(1))
          : extra.unit_price ?? product.price;
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price,
          image: product.image,
          metal: extra.metal || product.metal,
          chain: extra.chain || null,
          availability: extra.availability || null,
          lead_time: extra.lead_time || null,
          size,
          personalisation,
          ...extra,
          qty,
        },
      ];
    });
    toast.success("Added to basket", { description: product.name });
    setCartOpen(true);
  };

  const removeFromCart = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const setQty = (key, qty) =>
    setCart((prev) =>
      qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))
    );
  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      const has = prev.find((i) => i.id === product.id);
      toast.success(has ? "Removed from wishlist" : "Added to wishlist", { description: product.name });
      const next = has ? prev.filter((i) => i.id !== product.id) : [...prev, { id: product.id, slug: product.slug, name: product.name }];
      syncWishlist(next);
      return next;
    });
  };

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  return (
    <ShopContext.Provider
      value={{ cart, cartCount, subtotal, addToCart, removeFromCart, setQty, clearCart, wishlist, toggleWishlist, cartOpen, setCartOpen, syncAccountWishlist, recentlyViewed, trackView }}
    >
      {children}
    </ShopContext.Provider>
  );
}

export const useShop = () => useContext(ShopContext);
